/**
 * Shared label normalization and matching. Sort keys and drilldown columns
 * both map a configured or requested label onto an actual column, and they use
 * these helpers so a label that matches in one place matches the same way in
 * the other.
 */

/** "'Table'[Measure]" or "[Measure]" -> "Measure"; other labels are returned trimmed. */
export function stripTableQualifier(value: string | null | undefined) {
  const text = String(value ?? '')
  const match = text.match(/\[([^\]]+)\]/)
  return (match?.[1] ?? text).trim()
}

/**
 * Comparison token: lowercase letters, digits and "%" only. Case, spaces,
 * quotes, brackets and separators are ignored, and "%" is kept so "Margin %"
 * stays distinct from "Margin".
 */
export function normalizeLabel(value: string | null | undefined) {
  return String(value ?? '').toLowerCase().replace(/[^a-z0-9%]/g, '')
}

export function labelsMatch(left: string | null | undefined, right: string | null | undefined) {
  const token = normalizeLabel(left)
  return token !== '' && token === normalizeLabel(right)
}

/**
 * Fallback used when no entry matches exactly:
 * - 'none': exact matches only.
 * - 'first-suffix': the first entry whose label ends with the target, or the target with it.
 */
export type LabelFuzzyMatch = 'none' | 'first-suffix'

/** Finds the entry whose label(s) match `label`. An exact normalized match always wins. */
export function findByLabel<T>(
  entries: T[],
  label: string | null | undefined,
  labelsOf: (entry: T) => Array<string | null | undefined>,
  fuzzy: LabelFuzzyMatch = 'none',
): T | null {
  const target = normalizeLabel(label)
  if (!target) return null
  const tokenSets = entries.map((entry) => labelsOf(entry).map(normalizeLabel).filter(Boolean))

  const exactIndex = tokenSets.findIndex((tokens) => tokens.includes(target))
  if (exactIndex >= 0) return entries[exactIndex] ?? null
  if (fuzzy === 'none') return null

  const index = tokenSets.findIndex((tokens) => tokens.some((token) => token.endsWith(target) || target.endsWith(token)))
  return index >= 0 ? entries[index] ?? null : null
}
